import { Helmet } from "react-helmet-async";
import { canonicalUrl } from "@/lib/structured-data";

export {
  accessReportSeoMetadata,
  briefArchiveSeoMetadata,
  specialistSeoMetadata,
  specialtySeoMetadata,
} from "@/lib/structured-data";

/**
 * <SEO> — per-page meta + JSON-LD.
 *
 * Every page on PulseBoard renders one of these inside its main element.
 * index.html and the prerendered pages mark the same tags data-rh="true",
 * so Helmet replaces them rather than appending duplicates.
 *
 * Decisions:
 *  - Canonical URL always absolute. Prevents Google indexing the dev host
 *    or accidental trailing-slash variants.
 *  - `noindex` is exposed for preview pages + auth flows.
 *  - Twitter tags mirror OG — the twitter:card-specific values are what
 *    actually renders on X, so we duplicate rather than rely on OG fallbacks.
 *  - JSON-LD is stringified here (Helmet doesn't support <script> children
 *    of non-string type in v3).
 */

const SITE_URL = "https://canberra.pulseboard.com.au";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;
const DEFAULT_OG_IMAGE_ALT = "PulseBoard: find a specialist in Canberra";

export interface SEOProps {
  /** Full page title — will be shown as-is in the browser tab. */
  title: string;
  /** ≤160 chars, unique per page, no duplication across the site. */
  description: string;
  /** Absolute path (e.g. `/directory`). */
  path: string;
  /** Absolute URL of the OG image. Omit for default brand OG. */
  ogImage?: string;
  /** Alt text for the OG image. Defaults to the brand card's alt. */
  ogImageAlt?: string;
  /** ISO 8601 date if this page represents updatable content. */
  modifiedTime?: string;
  /** Used for articles / posts (not usually needed for SaaS pages). */
  publishedTime?: string;
  /** Set true on dashboards, auth pages, or anything patient-data-adjacent. */
  noindex?: boolean;
  /**
   * With `noindex`, still let crawlers follow links (`noindex,follow`). Use for
   * public pages that are temporarily thin but link to indexable pages.
   */
  follow?: boolean;
  /** og:type — "profile" on specialist pages, "website" otherwise. */
  ogType?: "website" | "profile" | "article";
  /** Structured data. Pass a single object or array of objects. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export function SEO({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  modifiedTime,
  publishedTime,
  noindex = false,
  follow = false,
  ogType = "website",
  jsonLd,
}: SEOProps) {
  const url = canonicalUrl(path);
  const robots = noindex
    ? follow
      ? "noindex,follow"
      : "noindex,nofollow"
    : "index,follow,max-image-preview:large";

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      {/* Dimensions are only known for the 1200×630 brand card. */}
      {ogImage === DEFAULT_OG_IMAGE && <meta property="og:image:width" content="1200" />}
      {ogImage === DEFAULT_OG_IMAGE && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={ogImageAlt} />
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
